import { useState, useEffect, useCallback } from "react";
import { FlashcardContent } from "../../../../../types/newtypes";

interface UseFlashcardHookProps {
  content: FlashcardContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

interface UseFlashcardHookReturn {
  flippedCards: Set<number>;
  currentCardIndex: number;
  allCardsFlipped: boolean;
  handleCardFlip: (index: number) => void;
  handlePreviousCard: () => void;
  handleNextCard: () => void;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
}

export const useFlashcardHook = ({
  content,
  onCardStateChange,
}: UseFlashcardHookProps): UseFlashcardHookReturn => {
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  // Check if all cards have been flipped
  const allCardsFlipped = flippedCards?.size === content?.items?.length;

  // Update card state when all cards are flipped
  useEffect(() => {
    if (allCardsFlipped) {
      onCardStateChange?.(true, true);
    } else {
      onCardStateChange?.(false, false);
    }
  }, [allCardsFlipped, onCardStateChange]);

  // Handle card flip (toggle between front and back)
  const handleCardFlip = useCallback((index: number) => {
    setFlippedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet?.has(index)) {
        newSet?.delete(index); // Allow flipping back to front
      } else {
        newSet?.add(index);
      }
      return newSet;
    });
  }, []);

  // Navigate to previous card (preserves flip state)
  const handlePreviousCard = useCallback(() => {
    setCurrentCardIndex((prev) => Math.max(0, prev - 1));
  }, []);

  // Navigate to next card (preserves flip state)
  const handleNextCard = useCallback(() => {
    setCurrentCardIndex((prev) =>
      Math.min(content?.items?.length - 1, prev + 1)
    );
  }, [content?.items?.length]);

  // Check if previous button should be disabled
  const isPreviousDisabled = currentCardIndex === 0;

  // Check if next button should be disabled
  const isNextDisabled =
    currentCardIndex === content?.items?.length - 1 ||
    !flippedCards?.has(currentCardIndex);

  return {
    flippedCards,
    currentCardIndex,
    allCardsFlipped,
    handleCardFlip,
    handlePreviousCard,
    handleNextCard,
    isPreviousDisabled,
    isNextDisabled,
  };
};
